import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TUTOR } from "@/constants/testIds";
import { Sparkles, Send, Plus, MessageSquare, User, Bot, Search, X, Mic } from "lucide-react";
import { toast } from "sonner";

const QUICK_ACTIONS = [
  { key: "eli10", label: "Explain like I'm 10", prompt: "Explain your last answer like I'm 10 years old." },
  { key: "another", label: "Give another example", prompt: "Give me another concrete example." },
  { key: "harder", label: "Make it harder", prompt: "Make it harder and quiz me on it." },
  { key: "practice", label: "Give me practice", prompt: "Give me a short practice exercise on this topic." },
  { key: "test", label: "Test me", prompt: "Quiz me with 3 targeted questions and grade my answers." },
  { key: "project", label: "Create a project", prompt: "Suggest a hands-on project I can build with this topic, in steps." },
];

// Session search — hits the new unified /sessions/search endpoint
function SessionSearch() {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [busy, setBusy] = useState(false);
  const doSearch = async (val) => {
    setQ(val);
    if (!val.trim()) { setHits([]); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/sessions/search?q=${encodeURIComponent(val.trim())}`);
      setHits(data || []);
    } catch { setHits([]); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="session-search-root">
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
        <input
          data-testid="session-search-input"
          value={q}
          onChange={(e) => doSearch(e.target.value)}
          placeholder="Search sessions…"
          className="w-full rounded-full border border-border bg-background pl-9 pr-8 py-2 text-xs font-mono focus:border-primary outline-none"
        />
        {q && (
          <button onClick={() => doSearch("")} className="absolute right-2 top-2 rounded-full p-0.5 hover:bg-muted">
            <X className="h-3 w-3 text-muted-foreground" />
          </button>
        )}
      </div>
      {q && (
        <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
          {busy && <div className="label-mono">searching…</div>}
          {!busy && hits.length === 0 && <div className="label-mono">no matches</div>}
          {hits.slice(0, 8).map((h) => (
            <a
              key={h.session_id}
              href={`/app/tutor/${h.session_id}`}
              data-testid={`session-search-hit-${h.session_id}`}
              className="block rounded-lg border border-border bg-background/60 p-2 hover:border-primary transition"
            >
              <div className="text-xs font-medium truncate">{h.title || "Learning Session"}</div>
              <div className="label-mono">{h.match_count} match{h.match_count !== 1 ? "es" : ""}</div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}


export default function TutorPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const mentorName = user?.mentor_profile?.mentor_name || "LaneX Mentor";
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null); // full session with messages
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    const { data } = await api.get("/tutor/sessions");
    setSessions(data);
    return data;
  };

  const openSession = async (id) => {
    const { data } = await api.get(`/tutor/sessions/${id}`);
    setCurrent(data);
    navigate(`/app/tutor/${id}`, { replace: true });
  };

  const createSession = async () => {
    const { data } = await api.post("/tutor/sessions", { title: "New Session" });
    await loadSessions();
    setCurrent(data);
    navigate(`/app/tutor/${data.session_id}`, { replace: true });
  };

  useEffect(() => {
    (async () => {
      const list = await loadSessions();
      if (sessionId) {
        await openSession(sessionId);
      } else if (list.length > 0) {
        await openSession(list[0].session_id);
      } else {
        await createSession();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [current?.messages?.length, sending]);

  const send = async (text) => {
    if (!current || !text.trim() || sending) return;
    setSending(true);
    const now = new Date().toISOString();
    const userMsg = { role: "user", content: text, ts: now };
    const optimisticAssistant = { role: "assistant", content: "", ts: now, streaming: true };
    setCurrent((c) => ({ ...c, messages: [...(c.messages || []), userMsg, optimisticAssistant] }));
    setInput("");
    let streamErrorDetail = null;
    try {
      const token = localStorage.getItem("lanex_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tutor/sessions/${current.session_id}/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let finalReply = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop() || "";
        for (const chunk of events) {
          const line = chunk.split("\n").find(l => l.startsWith("data: "));
          if (!line) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.delta) {
              setCurrent((c) => {
                const msgs = [...c.messages];
                msgs[msgs.length - 1] = { ...msgs[msgs.length - 1], content: (msgs[msgs.length - 1].content || "") + evt.delta };
                return { ...c, messages: msgs };
              });
            } else if (evt.done) {
              finalReply = evt.reply || "";
              if (!finalReply && evt.error) streamErrorDetail = evt.error;
              setCurrent((c) => {
                const msgs = [...c.messages];
                msgs[msgs.length - 1] = { ...msgs[msgs.length - 1], content: finalReply || msgs[msgs.length - 1].content, streaming: false };
                return { ...c, messages: msgs };
              });
            } else if (evt.error) {
              streamErrorDetail = evt.message || evt.error;
              throw new Error("stream error");
            }
          } catch { /* skip malformed frames */ }
        }
      }
      if (streamErrorDetail && !finalReply) throw new Error(streamErrorDetail);
      loadSessions();
    } catch (streamErr) {
      // Fallback: non-streaming — this path also surfaces the actual server error
      try {
        const { data } = await api.post(`/tutor/sessions/${current.session_id}/messages`, { text });
        setCurrent((c) => {
          const msgs = [...c.messages];
          msgs[msgs.length - 1] = { ...msgs[msgs.length - 1], content: data.assistant_message.content, streaming: false };
          return { ...c, messages: msgs };
        });
        loadSessions();
      } catch (fallbackErr) {
        // Roll back both the optimistic user bubble AND the empty assistant bubble
        setCurrent((c) => ({ ...c, messages: c.messages.slice(0, -2) }));
        const serverDetail =
          fallbackErr?.response?.data?.detail ||
          streamErrorDetail ||
          streamErr?.message ||
          "Your mentor is temporarily unavailable.";
        // Show the specific reason so it's clear whether it's a rate limit, key issue, quota, etc.
        toast.error(
          typeof serverDetail === "string" && serverDetail.length < 240
            ? `Mentor unavailable — ${serverDetail}`
            : "Your mentor is temporarily unavailable. Please try again in a moment."
        );
      }
    } finally { setSending(false); }
  };

  return (
    <div data-testid={TUTOR.root} className="grid gap-6 lg:grid-cols-[280px_1fr] animate-fade-up">
      {/* Sessions sidebar */}
      <aside className="rounded-2xl border border-border bg-card p-4 h-fit lg:sticky lg:top-6">
        <div className="mb-3 flex items-center justify-between">
          <div className="label-mono">// learning sessions</div>
          <Button data-testid={TUTOR.newSessionBtn} size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={createSession}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Search */}
        <SessionSearch />

        <div className="max-h-[60vh] space-y-1 overflow-y-auto pr-1 mt-3">
          {sessions.length === 0 && <div className="text-xs text-muted-foreground">No sessions yet.</div>}
          {sessions.map((s) => (
            <button
              key={s.session_id}
              data-testid={TUTOR.sessionItem(s.session_id)}
              onClick={() => openSession(s.session_id)}
              className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition ${current?.session_id === s.session_id ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
            >
              <div className="truncate font-medium">{s.title}</div>
              <div className="label-mono">{new Date(s.updated_at).toLocaleDateString()}</div>
            </button>
          ))}
        </div>
      </aside>

      {/* Chat */}
      <section className="flex min-h-[70vh] flex-col rounded-2xl border border-border bg-card">
        <header className="flex items-center justify-between border-b border-border p-4">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-lg border border-primary/40 bg-primary/10 text-primary">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display font-bold">{mentorName}</div>
              <div className="label-mono">Your personal mentor · Adaptive</div>
            </div>
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 space-y-6 overflow-y-auto p-6">
          {(!current || (current.messages || []).length === 0) && (
            <div className="rounded-xl border border-dashed border-border p-8 text-center">
              <MessageSquare className="mx-auto h-8 w-8 text-muted-foreground" />
              <div className="mt-2 font-display text-lg font-bold">Ask anything.</div>
              <div className="mt-1 text-xs text-muted-foreground">{mentorName} adapts to how you learn — talk, type, sketch or code (voice launching soon).</div>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {QUICK_ACTIONS.slice(0, 3).map((a) => (
                  <button
                    key={a.key}
                    data-testid={TUTOR.quickAction(a.key)}
                    onClick={() => send(a.prompt)}
                    className="rounded-full border border-border bg-secondary px-3 py-1 font-mono text-xs hover:border-primary"
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {(current?.messages || []).map((m, i) => (
            <div key={i} data-testid={TUTOR.message(i)} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "assistant" && (
                <div className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full border border-primary/40 bg-primary/10 text-primary">
                  <Bot className="h-4 w-4" />
                </div>
              )}
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "border border-border bg-muted" : "border border-primary/25 bg-primary/5"}`}>
                {m.content}{m.streaming ? <span className="chat-thinking" /> : null}
              </div>
              {m.role === "user" && (
                <div className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full border border-border bg-secondary">
                  <User className="h-4 w-4" />
                </div>
              )}
            </div>
          ))}
          {sending && !(current?.messages || []).some(m => m.streaming) && (
            <div className="flex gap-3">
              <div className="grid h-8 w-8 place-items-center rounded-full border border-primary/40 bg-primary/10 text-primary">
                <Bot className="h-4 w-4" />
              </div>
              <div className="rounded-2xl border border-primary/25 bg-primary/5 px-4 py-3 text-sm">
                <span className="chat-thinking">Thinking</span>
              </div>
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div className="border-t border-border p-3">
          <div className="mb-2 flex flex-wrap gap-2">
            {QUICK_ACTIONS.map((a) => (
              <button
                key={a.key}
                data-testid={TUTOR.quickAction(a.key)}
                onClick={() => send(a.prompt)}
                disabled={sending}
                className="rounded-full border border-border bg-background px-3 py-1 font-mono text-xs hover:border-primary disabled:opacity-50"
              >
                {a.label}
              </button>
            ))}
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); send(input); }}
            className="flex items-center gap-2"
          >
            <Input
              data-testid={TUTOR.input}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`Talk to ${mentorName}…`}
              disabled={sending}
              className="h-11 rounded-full"
            />
            <button
              type="button"
              data-testid="tutor-voice-btn"
              onClick={() => toast.info("Voice conversations arrive with Phase 2. Text works today.")}
              title="Voice mode — arriving in Phase 2"
              className="grid h-11 w-11 place-items-center rounded-full border border-border bg-card text-muted-foreground hover:border-primary hover:text-primary transition"
            >
              <Mic className="h-4 w-4" />
            </button>
            <Button
              data-testid={TUTOR.sendBtn}
              type="submit"
              disabled={sending || !input.trim()}
              className="h-11 rounded-full btn-neon px-5 font-mono text-xs uppercase tracking-widest"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
